import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import {
  GestureResponderEvent,
  LayoutChangeEvent,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  ViewStyle,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import Dialog from 'components/dialog';
import { Typography } from 'components/ui';
import {
  ANDROID_NAV_BAR_BOTTOM_INSET_FALLBACK,
  TAB_BAR_MIN_HEIGHT,
} from 'navigation/tab-bar-safe-area';
import { ZIndex, primitive, spacing } from 'styles/constants';
import { breakpoints } from 'styles/constants/breakpoints';

import { copyDiagnosticMarkdown } from '../context/clipboard';
import {
  clampDiagnosticLauncherPosition,
  getDefaultDiagnosticLauncherPosition,
  isDiagnosticDrag,
} from '../context/position';
import { useDiagnosticContext, useDiagnosticToolsEnabled } from '../context/provider';
import { DiagnosticLauncherPosition } from '../context/types';

import { DiagnosticContextPresentationLayer } from './diagnostic-context-presentation-layer';
import { DiagnosticLauncherGesture } from './diagnostic-launcher-gesture';
import { DiagnosticMarkdownPreview } from './diagnostic-markdown-preview';

const EDGE_MARGIN = 12;

type WebDragStyle = ViewStyle & { touchAction: 'none' };

const webDragStyle: WebDragStyle | undefined =
  Platform.OS === 'web' ? { cursor: 'pointer', touchAction: 'none' } : undefined;

const getGesturePoint = (event: GestureResponderEvent) => ({
  x: event.nativeEvent.pageX,
  y: event.nativeEvent.pageY,
});

export const DiagnosticContextSurface = ({ isModal = false }: { isModal?: boolean }) => {
  const enabled = useDiagnosticToolsEnabled();

  return enabled ? <EnabledDiagnosticContextSurface isModal={isModal} /> : null;
};

const EnabledDiagnosticContextSurface = ({ isModal }: { isModal: boolean }) => {
  const hostId = useId();
  const {
    activeHostId,
    close,
    isOpen,
    launcherPosition,
    markdown,
    open,
    refresh,
    registerHost,
    setLauncherPosition,
  } = useDiagnosticContext();
  const dimensions = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const isMobileViewport = dimensions.width < breakpoints.md;
  const [hostSize, setHostSize] = useState({ width: 0, height: 0 });
  const [launcherSize, setLauncherSize] = useState({ width: 0, height: 0 });
  const [transientPosition, setTransientPosition] = useState<DiagnosticLauncherPosition | null>(
    null
  );
  const [copyFeedback, setCopyFeedback] = useState<string>();
  const gestureOriginRef = useRef<DiagnosticLauncherPosition>({ x: 0, y: 0 });
  const gestureStartRef = useRef<DiagnosticLauncherPosition>({ x: 0, y: 0 });
  const gestureDeltaRef = useRef<DiagnosticLauncherPosition>({ x: 0, y: 0 });
  const didDragRef = useRef(false);
  const effectivePositionRef = useRef<DiagnosticLauncherPosition>({ x: 0, y: 0 });
  const openRef = useRef(open);
  const setLauncherPositionRef = useRef(setLauncherPosition);

  useEffect(() => registerHost(hostId, isModal), [hostId, isModal, registerHost]);

  const boundsInput = useMemo(
    () => ({
      viewportWidth: hostSize.width || dimensions.width,
      viewportHeight: hostSize.height || dimensions.height,
      launcherWidth: launcherSize.width,
      launcherHeight: launcherSize.height,
      insetTop: isModal && Platform.OS !== 'ios' ? 0 : insets.top,
      insetRight: insets.right,
      insetBottom: insets.bottom,
      insetLeft: insets.left,
      edgeMargin: EDGE_MARGIN,
      defaultBottomOffset:
        TAB_BAR_MIN_HEIGHT +
        (Platform.OS === 'android'
          ? Math.max(ANDROID_NAV_BAR_BOTTOM_INSET_FALLBACK - insets.bottom, 0)
          : 0),
    }),
    [dimensions.height, dimensions.width, hostSize, insets, isModal, launcherSize]
  );
  const boundsInputRef = useRef(boundsInput);
  const defaultPosition = useMemo(() => getDefaultDiagnosticLauncherPosition(boundsInput), [
    boundsInput,
  ]);
  const effectivePosition = transientPosition ?? launcherPosition ?? defaultPosition;
  const isActiveHost = activeHostId === hostId;

  effectivePositionRef.current = effectivePosition;
  boundsInputRef.current = boundsInput;
  openRef.current = open;
  setLauncherPositionRef.current = setLauncherPosition;

  useEffect(() => {
    if (
      !isActiveHost ||
      !launcherPosition ||
      launcherSize.width === 0 ||
      launcherSize.height === 0
    ) {
      return;
    }
    const clamped = clampDiagnosticLauncherPosition(launcherPosition, boundsInput);
    if (clamped.x !== launcherPosition.x || clamped.y !== launcherPosition.y) {
      setLauncherPosition(clamped);
    }
  }, [boundsInput, isActiveHost, launcherPosition, launcherSize, setLauncherPosition]);

  useEffect(() => {
    if (!isOpen) {
      setCopyFeedback(undefined);
    }
  }, [isOpen]);

  const getGestureDelta = (event: GestureResponderEvent): DiagnosticLauncherPosition => {
    const point = getGesturePoint(event);
    return {
      x: point.x - gestureStartRef.current.x,
      y: point.y - gestureStartRef.current.y,
    };
  };

  const getBoundedGesturePosition = useCallback(
    (delta: DiagnosticLauncherPosition) =>
      clampDiagnosticLauncherPosition(
        {
          x: gestureOriginRef.current.x + delta.x,
          y: gestureOriginRef.current.y + delta.y,
        },
        boundsInputRef.current
      ),
    []
  );

  const handleGestureBegin = useCallback(() => {
    gestureOriginRef.current = effectivePositionRef.current;
    gestureDeltaRef.current = { x: 0, y: 0 };
    didDragRef.current = false;
  }, []);

  const handleGestureUpdate = useCallback(
    (delta: DiagnosticLauncherPosition) => {
      gestureDeltaRef.current = delta;
      if (!didDragRef.current && isDiagnosticDrag(delta.x, delta.y)) {
        didDragRef.current = true;
      }
      if (didDragRef.current) {
        setTransientPosition(getBoundedGesturePosition(delta));
      }
    },
    [getBoundedGesturePosition]
  );

  const commitDrag = useCallback(() => {
    setLauncherPositionRef.current(getBoundedGesturePosition(gestureDeltaRef.current));
    setTransientPosition(null);
  }, [getBoundedGesturePosition]);

  const handleGestureEnd = useCallback(
    (delta: DiagnosticLauncherPosition) => {
      gestureDeltaRef.current = delta;
      if (didDragRef.current || isDiagnosticDrag(delta.x, delta.y)) {
        commitDrag();
        return;
      }
      setTransientPosition(null);
      openRef.current();
    },
    [commitDrag]
  );

  const handleGestureCancel = useCallback(() => {
    if (didDragRef.current) {
      commitDrag();
      return;
    }
    setTransientPosition(null);
  }, [commitDrag]);

  const handleResponderGrant = (event: GestureResponderEvent) => {
    handleGestureBegin();
    gestureStartRef.current = getGesturePoint(event);
  };

  const handleResponderMove = (event: GestureResponderEvent) => {
    const delta = getGestureDelta(event);
    handleGestureUpdate(delta);
  };

  const handleResponderRelease = (event: GestureResponderEvent) => {
    handleGestureEnd(getGestureDelta(event));
  };

  const handleResponderTerminate = handleGestureCancel;

  const handleLauncherLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    if (width !== launcherSize.width || height !== launcherSize.height) {
      setLauncherSize({ width, height });
    }
  };

  const handleHostLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    setHostSize(current =>
      current.width === width && current.height === height ? current : { width, height }
    );
  };

  const handleCopy = async () => {
    if (!markdown) {
      return;
    }
    const result = await copyDiagnosticMarkdown(markdown);
    setCopyFeedback(result.ok ? 'Copied Jira context.' : result.reason);
  };

  const handleRefresh = () => {
    setCopyFeedback(undefined);
    refresh();
  };

  if (!isActiveHost) {
    return null;
  }

  return (
    <>
      <DiagnosticContextPresentationLayer>
        <View
          onLayout={handleHostLayout}
          pointerEvents="box-none"
          style={styles.overlay}
          testID="diagnostic-context-host"
        >
          <DiagnosticLauncherGesture
            onCancel={handleGestureCancel}
            onEnd={handleGestureEnd}
            onStart={handleGestureBegin}
            onUpdate={handleGestureUpdate}
          >
            <View
              accessible
              accessibilityActions={[{ name: 'activate', label: 'Open diagnostic context' }]}
              accessibilityHint="Drag to reposition. Activate to capture current diagnostic context."
              accessibilityLabel="Jira diagnostic context"
              accessibilityRole="button"
              onAccessibilityAction={event => {
                if (event.nativeEvent.actionName === 'activate') {
                  open();
                }
              }}
              onLayout={handleLauncherLayout}
              {...(Platform.OS === 'web'
                ? {
                    onMoveShouldSetResponder: () => true,
                    onResponderGrant: handleResponderGrant,
                    onResponderMove: handleResponderMove,
                    onResponderRelease: handleResponderRelease,
                    onResponderTerminate: handleResponderTerminate,
                    onResponderTerminationRequest: () => false,
                    onStartShouldSetResponder: () => true,
                    onStartShouldSetResponderCapture: () => true,
                  }
                : undefined)}
              style={[
                styles.launcher,
                isMobileViewport ? styles.mobileLauncher : null,
                webDragStyle,
                {
                  transform: [
                    { translateX: effectivePosition.x },
                    { translateY: effectivePosition.y },
                  ],
                },
              ]}
              testID="jira-diagnostic-context-launcher"
            >
              <Typography style={styles.launcherText}>
                {isMobileViewport ? 'QA' : 'QA Context'}
              </Typography>
            </View>
          </DiagnosticLauncherGesture>
        </View>
      </DiagnosticContextPresentationLayer>

      {/* Dialog opens its own native Modal (NativeBase AlertDialog). Rendering it inside
          DiagnosticContextPresentationLayer would nest that Modal inside the iOS
          FullWindowOverlay / Android native Modal used for the launcher above — two
          competing native presentation layers. A Modal's stacking is determined by native
          presentation order, not React tree position, so rendering it here instead still
          presents above whatever else is currently open. */}
      <Dialog
        actions={
          <View style={styles.actions} testID="diagnostic-context-actions">
            <Pressable
              accessibilityLabel="Copy Jira context"
              accessibilityRole="button"
              onPress={handleCopy}
              style={[
                styles.actionButton,
                isMobileViewport ? styles.mobileActionButton : styles.desktopActionButton,
              ]}
              testID="diagnostic-context-copy"
            >
              <Typography style={styles.actionText}>
                {isMobileViewport ? 'Copy' : 'Copy Jira context'}
              </Typography>
            </Pressable>
            <Pressable
              accessibilityLabel="Refresh context"
              accessibilityRole="button"
              onPress={handleRefresh}
              style={[
                styles.actionButton,
                isMobileViewport ? styles.mobileActionButton : styles.desktopActionButton,
              ]}
              testID="diagnostic-context-refresh"
            >
              <Typography style={styles.actionText}>
                {isMobileViewport ? 'Refresh' : 'Refresh context'}
              </Typography>
            </Pressable>
            <Pressable
              accessibilityLabel="Close"
              accessibilityRole="button"
              onPress={close}
              style={[
                styles.closeButton,
                isMobileViewport ? styles.mobileActionButton : styles.desktopCloseButton,
              ]}
              testID="diagnostic-context-close"
            >
              <Typography style={styles.closeText}>Close</Typography>
            </Pressable>
          </View>
        }
        contentMaxWidth="760px"
        contentWidth="90%"
        heading="Jira diagnostic context"
        maxHeight="90%"
        modalAppearanceEventMessage="Internal diagnostic context opened"
        onDismiss={close}
        showDialog={isOpen}
        testID="jira-diagnostic-context-dialog"
      >
        <View style={styles.dialogBody} testID="diagnostic-context-body">
          <ScrollView
            contentContainerStyle={styles.markdownContent}
            style={styles.markdownScroll}
            testID="jira-diagnostic-context-content"
          >
            <DiagnosticMarkdownPreview markdown={markdown ?? ''} />
          </ScrollView>
          {!!copyFeedback && (
            <Typography accessibilityLiveRegion="polite" style={styles.feedback}>
              {copyFeedback}
            </Typography>
          )}
        </View>
      </Dialog>
    </>
  );
};

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: ZIndex.TOP,
  },
  launcher: {
    position: 'absolute',
    left: 0,
    top: 0,
    minWidth: 92,
    minHeight: 44,
    paddingHorizontal: spacing.$3,
    paddingVertical: spacing.$2,
    borderRadius: primitive.$borderRadius,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: primitive.$bbqBrown,
    shadowColor: primitive.$black,
    shadowOpacity: 0.24,
    shadowRadius: 5,
    elevation: 8,
    userSelect: 'none',
  },
  launcherText: {
    color: primitive.$white,
    fontWeight: '700',
  },
  mobileLauncher: {
    minWidth: 44,
    paddingHorizontal: spacing.$2,
  },
  dialogBody: {
    flexShrink: 1,
    minHeight: 0,
    paddingHorizontal: spacing.$4,
    gap: spacing.$2,
  },
  markdownScroll: {
    flexShrink: 1,
    minHeight: 0,
    maxHeight: 420,
    borderWidth: 1,
    borderColor: primitive.$blackOpacity10,
    borderRadius: primitive.$borderRadius,
  },
  markdownContent: {
    padding: spacing.$3,
  },
  feedback: {
    color: primitive.$bbqBrown,
  },
  actions: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: 'flex-end',
    gap: spacing.$2,
    paddingTop: spacing.$3,
  },
  actionButton: {
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: primitive.$borderRadius,
    backgroundColor: primitive.$fieryRed,
  },
  actionText: {
    color: primitive.$white,
    fontWeight: '700',
    textAlign: 'center',
  },
  closeButton: {
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: primitive.$borderRadius,
    borderWidth: 1,
    borderColor: primitive.$bbqBrown,
  },
  closeText: {
    color: primitive.$bbqBrown,
    fontWeight: '700',
    textAlign: 'center',
  },
  mobileActionButton: {
    flex: 1,
    minWidth: 0,
    paddingHorizontal: spacing.$2,
  },
  desktopActionButton: {
    minWidth: 156,
    paddingHorizontal: spacing.$4,
  },
  desktopCloseButton: {
    minWidth: 112,
    paddingHorizontal: spacing.$4,
  },
});
